import React, { useState, useMemo } from 'react';
import { UserPlus, Trash2, Shield, Mail, Crown, UserCheck, RefreshCw, Download, Calendar, Briefcase, Building2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useAdmins, useAdminMutations, useEvents, useSchools, useLogMutations } from '../../hooks/useConvex';
import { useAuth } from '../../contexts/AuthContext';
import { AdminRole, Event, AdminUser, School } from '../../types';
import { Id } from '../../../convex/_generated/dataModel';

export const AdminsPage: React.FC = () => {
    const { user, adminRole } = useAuth();
    const adminsData = useAdmins();
    const eventsData = useEvents();
    const schoolsData = useSchools();
    const { add, deleteAdmin } = useAdminMutations();
    const { logAdminActivity } = useLogMutations();
    const loading = adminsData === undefined || eventsData === undefined || schoolsData === undefined;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [newAdminRole, setNewAdminRole] = useState<AdminRole>('admin');
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const [selectedSchool, setSelectedSchool] = useState<string>('');
    const [actionLoading, setActionLoading] = useState(false);
    const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

    const isSupremeAdmin = adminRole === 'supreme_admin' || adminRole === 'principal' || adminRole === 'school_admin_office';
    const isChief = user?.email === 'chaitanyamishra.ai@gmail.com';

    // Transform Convex data and sort admins
    const admins = useMemo(() => {
        if (!adminsData) return [];
        const transformed = adminsData.map((a: Record<string, unknown>) => ({
            ...a,
            admin_id: a._id,
        })) as AdminUser[];

        // Sort to pin chief at the top
        return transformed.sort((a: AdminUser, b: AdminUser) => {
            const aIsChief = a.email === 'chaitanyamishra.ai@gmail.com';
            const bIsChief = b.email === 'chaitanyamishra.ai@gmail.com';
            if (aIsChief) return -1;
            if (bIsChief) return 1;
            return 0;
        });
    }, [adminsData]);

    const events = useMemo(() => {
        if (!eventsData) return [];
        return eventsData.map((e: Record<string, unknown>) => ({
            ...e,
            event_id: e._id,
        })) as Event[];
    }, [eventsData]);

    const schools = useMemo(() => {
        if (!schoolsData) return [];
        return schoolsData.map((s: Record<string, unknown>) => ({
            ...s,
            school_id: s._id,
        })) as School[];
    }, [schoolsData]);

    const handleAddAdmin = async () => {
        if (!newAdminEmail.trim()) {
            alert('Please enter an email address');
            return;
        }

        try {
            setActionLoading(true);
            await add({
                email: newAdminEmail.trim(),
                role: newAdminRole,
                addedBy: user?.email || '',
                assignedEvents: newAdminRole === 'event_manager' ? selectedEvents : undefined,
                assignedSchoolId: newAdminRole === 'escort' ? (selectedSchool as Id<'schools'>) : undefined,
            });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'add_admin',
                details: `Added new admin: ${newAdminEmail.trim()} (${newAdminRole})`,
            });

            setNewAdminEmail('');
            setSelectedEvents([]);
            setSelectedSchool('');
            setIsModalOpen(false);
        } catch (error: any) {
            alert(error.message || 'Failed to add admin');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveAdmin = async (email: string) => {
        if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE admin "${email}"? This action cannot be undone.`)) {
            return;
        }

        setDeletingEmail(email);
        try {
            await deleteAdmin({ email, deletedBy: user?.email || '' });

            // Log activity
            await logAdminActivity({
                email: user?.email || '',
                action: 'delete_admin',
                details: `Permanently deleted admin: ${email}`,
            });
        } catch (error: any) {
            console.error('Error deleting admin:', error);
            alert(error.message || 'Failed to delete admin');
        } finally {
            setDeletingEmail(null);
        }
    };

    const handleExportPDF = () => {
        const data = admins.map(admin => [
            admin.email,
            admin.role.replace('_', ' ').toUpperCase(),
            admin.added_by || 'System',
            admin.added_at ? new Date(admin.added_at).toLocaleDateString() : 'N/A',
            admin.is_active ? 'Active' : 'Inactive'
        ]);

        pdfService.generatePDF({
            title: 'Admin Users',
            filename: 'admin_users',
            columns: ['Email', 'Role', 'Added By', 'Date Added', 'Status'],
            data: data,
            orientation: 'landscape',
        });
    };

    const toggleEventSelection = (eventId: string) => {
        setSelectedEvents(prev =>
            prev.includes(eventId)
                ? prev.filter(id => id !== eventId)
                : [...prev, eventId]
        );
    };

    const canManageRole = (targetRole: AdminRole | string) => {
        if (isChief) return true;
        if (adminRole === 'principal') return targetRole !== 'supreme_admin';
        if (adminRole === 'school_admin_office') return !['principal', 'school_admin_office', 'supreme_admin'].includes(targetRole);
        if (adminRole === 'supreme_admin') return !['principal', 'school_admin_office', 'supreme_admin'].includes(targetRole);
        return false;
    };

    if (!isSupremeAdmin && !isChief) {
        return (
            <div className="h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-danger/[0.08] border border-danger/[0.12] flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-danger/40" />
                </div>
                <h2 className="text-xl font-display font-bold text-white mb-2">Access Restricted</h2>
                <p className="text-sm text-white/30 max-w-md text-center">
                    Only Supreme Admins, Principals, and School Admin Office can manage other administrators.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Shield className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Admin Management</h1>
                            <p className="text-sm text-white/30 mt-0.5">Manage system administrators and permissions</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            isLoading={loading}
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportPDF}>
                            <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add Admin
                        </Button>
                    </div>
                </div>
            </div>

            {/* Admins Table */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b border-white/[0.06] flex justify-between items-center">
                    <h2 className="text-sm font-display font-semibold text-white">System Administrators</h2>
                    <Badge variant="neutral" size="sm">{admins.filter(a => a.is_active).length} Active</Badge>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-white/[0.02]">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Admin User</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Role</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Added By</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Date</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Status</th>
                                <th className="px-6 py-3.5 text-right text-[11px] font-medium uppercase tracking-wider text-white/25">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <Loader size="md" className="mx-auto" />
                                    </td>
                                </tr>
                            ) : admins.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-16 text-center text-white/30">
                                        No admins found
                                    </td>
                                </tr>
                            ) : (
                                admins.map((admin) => {
                                    const isChiefUser = admin.email === 'chaitanyamishra.ai@gmail.com';
                                    const canDelete = isChief || (admin.email !== 'chaitanyamishra.ai@gmail.com' && canManageRole(admin.role));
                                    return (
                                        <tr key={admin.email} className="hover:bg-white/[0.02] transition-colors duration-150">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                                                        isChiefUser || admin.role === 'supreme_admin'
                                                            ? 'bg-brand/[0.1] border border-brand/20'
                                                            : 'bg-white/[0.03] border border-white/[0.06]'
                                                    }`}>
                                                        {isChiefUser || admin.role === 'supreme_admin'
                                                            ? <Crown className="w-4 h-4 text-brand" />
                                                            : <UserCheck className="w-4 h-4 text-white/40" />
                                                        }
                                                    </div>
                                                    <div>
                                                        <p className={`font-medium ${isChiefUser ? 'text-brand' : 'text-white'}`}>
                                                            {admin.email}
                                                        </p>
                                                        {admin.role === 'event_manager' && admin.assigned_events && admin.assigned_events.length > 0 && (
                                                            <p className="text-xs text-white/25 mt-1 flex items-center gap-1">
                                                                <Calendar className="w-3 h-3" />
                                                                {admin.assigned_events.length} Events
                                                            </p>
                                                        )}
                                                        {admin.role === 'escort' && admin.assigned_school_id && (
                                                            <p className="text-xs text-white/25 mt-1 flex items-center gap-1">
                                                                <Building2 className="w-3 h-3" />
                                                                {schools.find(s => s.school_id === admin.assigned_school_id)?.school_name || 'Unknown'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {isChiefUser ? (
                                                    <Badge variant="warning" size="sm">
                                                        <Crown className="w-3 h-3 mr-1" />
                                                        Chief
                                                    </Badge>
                                                ) : (
                                                    <Badge variant={admin.role === 'supreme_admin' ? 'warning' : 'info'} size="sm">
                                                        {admin.role.replace('_', ' ')}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-white/40">
                                                {admin.added_by || 'System'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-white/25 font-mono">
                                                {admin.added_at ? new Date(admin.added_at).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {isChiefUser ? (
                                                    <Badge variant="info" size="sm">Developer</Badge>
                                                ) : (
                                                    <Badge variant={admin.is_active ? 'success' : 'error'} size="sm">
                                                        {admin.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {canDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveAdmin(admin.email)}
                                                        className="text-white/30 hover:text-danger hover:bg-danger/10"
                                                        isLoading={deletingEmail === admin.email}
                                                        disabled={!!deletingEmail}
                                                        title="Permanently Delete"
                                                    >
                                                        {deletingEmail !== admin.email && <Trash2 className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Add Admin Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Add New Administrator"
                size="lg"
            >
                <div className="space-y-6">
                    <div>
                        <Input
                            label="Email Address"
                            value={newAdminEmail}
                            onChange={(e) => setNewAdminEmail(e.target.value)}
                            placeholder="admin@example.com"
                            type="email"
                            fullWidth
                            leftIcon={<Mail className="w-4 h-4" />}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white/60 mb-3">
                            Role
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div
                                className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                    newAdminRole === 'admin'
                                        ? 'border-brand bg-brand/[0.04]'
                                        : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                }`}
                                onClick={() => setNewAdminRole('admin')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <UserCheck className="w-4 h-4 text-brand" />
                                    <span className="font-semibold text-sm text-white">Admin</span>
                                </div>
                                <p className="text-xs text-white/40">Full access to manage events, schools, and results</p>
                            </div>

                            <div
                                className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                    newAdminRole === 'event_manager'
                                        ? 'border-brand bg-brand/[0.04]'
                                        : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                }`}
                                onClick={() => setNewAdminRole('event_manager')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Calendar className="w-4 h-4 text-brand" />
                                    <span className="font-semibold text-sm text-white">Event Manager</span>
                                </div>
                                <p className="text-xs text-white/40">Manage specific events and registrations</p>
                            </div>

                            <div
                                className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                    newAdminRole === 'escort'
                                        ? 'border-brand bg-brand/[0.04]'
                                        : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                }`}
                                onClick={() => setNewAdminRole('escort')}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <UserCheck className="w-4 h-4 text-brand" />
                                    <span className="font-semibold text-sm text-white">Escort</span>
                                </div>
                                <p className="text-xs text-white/40">Limited access for school coordination</p>
                            </div>

                            {(isChief || adminRole === 'principal') && (
                                <div
                                    className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                        newAdminRole === 'school_admin_office'
                                            ? 'border-brand bg-brand/[0.04]'
                                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                    }`}
                                    onClick={() => setNewAdminRole('school_admin_office')}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Building2 className="w-4 h-4 text-brand" />
                                        <span className="font-semibold text-sm text-white">School Office</span>
                                    </div>
                                    <p className="text-xs text-white/40">Manage admins and event managers</p>
                                </div>
                            )}

                            {isChief && (
                                <>
                                    <div
                                        className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                            newAdminRole === 'principal'
                                                ? 'border-brand bg-brand/[0.04]'
                                                : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                        }`}
                                        onClick={() => setNewAdminRole('principal')}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Briefcase className="w-4 h-4 text-brand" />
                                            <span className="font-semibold text-sm text-white">Principal</span>
                                        </div>
                                        <p className="text-xs text-white/40">Full system access and oversight</p>
                                    </div>

                                    <div
                                        className={`cursor-pointer p-4 rounded-xl border transition-all ${
                                            newAdminRole === 'supreme_admin'
                                                ? 'border-brand bg-brand/[0.04]'
                                                : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
                                        }`}
                                        onClick={() => setNewAdminRole('supreme_admin')}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Crown className="w-4 h-4 text-brand" />
                                            <span className="font-semibold text-sm text-white">Supreme Admin</span>
                                        </div>
                                        <p className="text-xs text-white/40">Complete system control</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {newAdminRole === 'event_manager' && (
                        <div className="animate-fade-in">
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Assign Events
                            </label>
                            <Card className="p-4 max-h-60 overflow-y-auto space-y-2">
                                {events.map(event => (
                                    <div
                                        key={event.event_id}
                                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                            selectedEvents.includes(event.event_id)
                                                ? 'bg-brand/[0.06] border border-brand/20'
                                                : 'hover:bg-white/[0.03] border border-transparent'
                                        }`}
                                        onClick={() => toggleEventSelection(event.event_id)}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                            selectedEvents.includes(event.event_id)
                                                ? 'bg-brand border-brand'
                                                : 'border-white/20'
                                        }`}>
                                            {selectedEvents.includes(event.event_id) && <UserCheck className="w-3 h-3 text-white" />}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-white">{event.name}</p>
                                            <p className="text-xs text-white/30">{event.category}</p>
                                        </div>
                                    </div>
                                ))}
                            </Card>
                            <p className="text-xs text-white/30 mt-2">
                                Selected: {selectedEvents.length} events
                            </p>
                        </div>
                    )}

                    {newAdminRole === 'escort' && (
                        <div className="animate-fade-in">
                            <label className="block text-sm font-medium text-white/60 mb-2">
                                Assign School
                            </label>
                            <select
                                value={selectedSchool}
                                onChange={(e) => setSelectedSchool(e.target.value)}
                                className="w-full px-4 py-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] text-white focus:border-brand/50 focus:outline-none appearance-none"
                            >
                                <option value="" className="bg-[#0A0A0A]">Select a school...</option>
                                {schools.map(school => (
                                    <option key={school.school_id} value={school.school_id} className="bg-[#0A0A0A]">
                                        {school.school_name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-white/30 mt-2">
                                Escorts can only manage participants for their assigned school
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddAdmin}
                            isLoading={actionLoading}
                            disabled={!newAdminEmail}
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add Admin
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
